// InsightSparkline — compact confidence trend across recent nightly runs.
// Used as a footer accent on InsightCardItem. Fetches
// /api/ai/insights/history?type=<insight_type>&n=14 and renders an inline SVG.

import { useEffect, useState } from "react";

interface SeriesPoint {
  date: string;
  confidence: number;
  rank: number;
}

interface HistoryResponse {
  runs: Array<{ run_id: string; generated_at: string }>;
  series: Array<{ type: string; points: SeriesPoint[] }>;
}

interface InsightSparklineProps {
  insightType: string;
  width?: number;
  height?: number;
}

export default function InsightSparkline({
  insightType,
  width = 80,
  height = 18,
}: InsightSparklineProps) {
  const [points, setPoints] = useState<SeriesPoint[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/ai/insights/history?type=${encodeURIComponent(insightType)}&n=14`,
          { credentials: "same-origin" },
        );
        if (!r.ok) return;
        const body = (await r.json()) as HistoryResponse;
        const series = body.series?.find((s) => s.type === insightType);
        if (cancelled) return;
        setPoints(series?.points ?? []);
      } catch {
        if (!cancelled) setPoints([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [insightType]);

  if (!points || points.length < 2) return null;

  const vals = points.map((p) => p.confidence);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const stepX = (width - 2) / (points.length - 1);

  const path = points
    .map((p, i) => {
      const x = 1 + i * stepX;
      const y = height - 1 - ((p.confidence - min) / range) * (height - 2);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  const lastX = 1 + (points.length - 1) * stepX;
  const lastY =
    height -
    1 -
    ((points[points.length - 1].confidence - min) / range) * (height - 2);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={`Confidence trend over last ${points.length} runs`}
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.25}
        opacity={0.7}
      />
      <circle cx={lastX} cy={lastY} r={1.6} fill="currentColor" />
    </svg>
  );
}
